import { StateCreator } from 'zustand';
import { PriceState } from '../types';
import { UserState } from '../../auth/userSlice';
import { debugLog } from '@/utils/debugLogger';

type SetState = Parameters<StateCreator<PriceState & UserState>>[0];
type GetState = Parameters<StateCreator<PriceState & UserState>>[1];

/**
 * Initialize store with SSR data from window.__HOME_PAGE_DATA__ or window.__METAL_PRICES__
 * Supports both spot-standard shape ({ price }) and dealer-specific shape ({ marketPrice })
 */
export const initializeFromSSR = (set: SetState, get: GetState) => {
  if (typeof window === 'undefined') return;
  
  const homePageData = (window as any).__HOME_PAGE_DATA__;
  const metalPrices = (window as any).__METAL_PRICES__;
  
  if (!homePageData && !metalPrices) {
    debugLog('general', '[SSR] No SSR data found, skipping initialization', undefined, 'initializeFromSSR');
    return;
  }
  
  debugLog('general', '[SSR] Initializing store with SSR data', { 
    hasHomePageData: !!homePageData,
    hasMetalPrices: !!metalPrices 
  }, 'initializeFromSSR');
  
  const updates: Partial<PriceState> = {};
  
  // Use metal prices data if available (more detailed)
  // Support both spot-standard shape (price) and dealer-specific shape (marketPrice)
  if (metalPrices?.gold || metalPrices?.silver) {
    if (metalPrices.gold) {
      const goldPrice = metalPrices.gold.price ?? metalPrices.gold.marketPrice ?? 0;
      updates.currentGoldPrice = {
        price: goldPrice,
        change: metalPrices.gold.change || 0,
        changePercentage: metalPrices.gold.changePercentage || 0,
        timestamp: metalPrices.gold.timestamp || ''
      };
      updates.rawGoldPrice = updates.currentGoldPrice;
      updates.goldPrice = goldPrice;
    }
    
    if (metalPrices.silver) {
      const silverPrice = metalPrices.silver.price ?? metalPrices.silver.marketPrice ?? 0;
      updates.currentSilverPrice = {
        price: silverPrice,
        change: metalPrices.silver.change || 0,
        changePercentage: metalPrices.silver.changePercentage || 0,
        timestamp: metalPrices.silver.timestamp || ''
      };
      updates.rawSilverPrice = updates.currentSilverPrice;
      updates.silverPrice = silverPrice;
    }
  }
  // Fallback to home page data
  else if (homePageData?.metalPrices) {
    if (homePageData.metalPrices.gold) {
      updates.currentGoldPrice = {
        price: homePageData.metalPrices.gold.price,
        change: 0,
        changePercentage: 0,
        timestamp: homePageData.metalPrices.gold.timestamp || ''
      };
      updates.rawGoldPrice = updates.currentGoldPrice;
      updates.goldPrice = homePageData.metalPrices.gold.price;
    }
    
    if (homePageData.metalPrices.silver) {
      updates.currentSilverPrice = {
        price: homePageData.metalPrices.silver.price,
        change: 0,
        changePercentage: 0,
        timestamp: homePageData.metalPrices.silver.timestamp || ''
      };
      updates.rawSilverPrice = updates.currentSilverPrice;
      updates.silverPrice = homePageData.metalPrices.silver.price;
    }
  }
  
  // Set loading to false since we have data
  updates.isPriceLoading = false;
  updates.error = null;
  
  debugLog('general', '[SSR] Setting store state with', updates, 'initializeFromSSR');
  set(updates);
};
